import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../Firebase';
import { Calendar, Edit2, Save, X, ChevronLeft, ChevronRight } from 'lucide-react';

const JOURS = ['Lundi','Mardi','Mercredi','Jeudi','Vendredi','Samedi'];
const MODULES = ['Grammar','Conjugating','Reading & Speaking'];
const NIVEAUX = ['A1','A2','B1','B2'];
const COULEURS = {
  'Grammar':            { bg:'#EEF2FF', border:'#818CF8', text:'#3730A3' },
  'Conjugating':        { bg:'#F0FDF4', border:'#4ADE80', text:'#166534' },
  'Reading & Speaking': { bg:'#FFF7ED', border:'#FB923C', text:'#9A3412' },
};

function getWeekDates(offset = 0) {
  const now = new Date();
  const day = now.getDay();
  const monday = new Date(now);
  monday.setDate(now.getDate() - (day === 0 ? 6 : day - 1) + offset * 7);
  return JOURS.map((_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });
}

function weekKey(dates) {
  return dates[0].toISOString().slice(0, 10);
}

export default function AdminPlanning({ staff }) {
  const canEdit = ['general_manager', 'it_manager'].includes(staff?.role);
  const [weekOffset, setWeekOffset] = useState(0);
  const [dates, setDates] = useState(getWeekDates(0));
  const [planning, setPlanning] = useState({});
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { jour, slot }
  const [editForm, setEditForm] = useState({ trainer: '', module: '', niveau: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    const d = getWeekDates(weekOffset);
    setDates(d);
    fetchPlanning(weekKey(d));
  }, [weekOffset]);

  useEffect(() => { fetchStaff(); }, []);

  async function fetchStaff() {
    const snap = await getDocs(collection(db, 'staff'));
    const trainers = snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(s => s.actif);
    setStaffList(trainers);
  }

  async function fetchPlanning(key) {
    setLoading(true);
    try {
      const snap = await getDoc(doc(db, 'planning', key));
      if (snap.exists()) setPlanning(snap.data().slots || {});
      else setPlanning({});
    } catch(e) { console.error(e); }
    setLoading(false);
  }

  async function savePlanning(newPlanning) {
    setSaving(true);
    const key = weekKey(dates);
    await setDoc(doc(db, 'planning', key), {
      slots: newPlanning,
      semaine: key,
      updatedAt: serverTimestamp(),
      updatedBy: staff?.nom,
    });
    setPlanning(newPlanning);
    setSaving(false);
    setMsg('Planning enregistré.');
    setTimeout(() => setMsg(''), 2000);
  }

  function openEdit(jour, slotKey) {
    const existing = planning[`${jour}_${slotKey}`] || {};
    setEditForm({
      trainer: existing.trainer || '',
      module:  existing.module  || '',
      niveau:  existing.niveau  || '',
      notes:   existing.notes   || '',
    });
    setEditing({ jour, slotKey });
  }

  async function handleSaveSlot() {
    if (!editForm.trainer || !editForm.module) return;
    const key = `${editing.jour}_${editing.slotKey}`;
    const newPlanning = { ...planning, [key]: { ...editForm, jour: editing.jour } };
    await savePlanning(newPlanning);
    setEditing(null);
  }

  async function handleDeleteSlot(jour, slotKey) {
    const key = `${jour}_${slotKey}`;
    const newPlanning = { ...planning };
    delete newPlanning[key];
    await savePlanning(newPlanning);
  }

  const slots = ['Matin (8h-12h)', 'Après-midi (14h-18h)', 'Soir (18h-20h)'];

  return (
    <div className="admin-page admin-planning">
      <div className="admin-page__header">
        <div>
          <h2>Planning hebdomadaire</h2>
          <p>{canEdit ? 'Gérez le programme de la semaine' : 'Programme de la semaine — lecture seule'}</p>
        </div>
        <div style={{display:'flex',gap:'0.5rem',alignItems:'center'}}>
          {msg && <span style={{fontSize:'0.82rem',color:'#2f9e44',fontWeight:600}}>{msg}</span>}
          <button className="admin-btn admin-btn--secondary" onClick={() => setWeekOffset(w => w-1)}>
            <ChevronLeft size={16}/>
          </button>
          <button className="admin-btn admin-btn--secondary" onClick={() => setWeekOffset(0)} style={{fontSize:'0.8rem'}}>
            Cette semaine
          </button>
          <button className="admin-btn admin-btn--secondary" onClick={() => setWeekOffset(w => w+1)}>
            <ChevronRight size={16}/>
          </button>
        </div>
      </div>

      {/* Modal édition */}
      {editing && canEdit && (
        <div className="admin-planning-modal-overlay" onClick={() => setEditing(null)}>
          <div className="admin-planning-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-planning-modal__header">
              <h3>✏️ {editing.jour} — {editing.slotKey}</h3>
              <button onClick={() => setEditing(null)}><X size={18}/></button>
            </div>
            <div className="admin-form-grid">
              <div className="admin-form-field">
                <label>Formateur</label>
                <select value={editForm.trainer} onChange={e => setEditForm(p => ({...p, trainer: e.target.value}))}>
                  <option value="">Sélectionner...</option>
                  {staffList.filter(s => ['trainer','general_manager','it_manager'].includes(s.role)).map(s => (
                    <option key={s.id} value={s.nom}>{s.nom} — {s.role}</option>
                  ))}
                </select>
              </div>
              <div className="admin-form-field">
                <label>Module</label>
                <select value={editForm.module} onChange={e => setEditForm(p => ({...p, module: e.target.value}))}>
                  <option value="">Sélectionner...</option>
                  {MODULES.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div className="admin-form-field">
                <label>Niveau</label>
                <select value={editForm.niveau} onChange={e => setEditForm(p => ({...p, niveau: e.target.value}))}>
                  <option value="">Tous</option>
                  {NIVEAUX.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <div className="admin-form-field">
                <label>Notes</label>
                <input type="text" value={editForm.notes}
                  onChange={e => setEditForm(p => ({...p, notes: e.target.value}))}
                  placeholder="Ex: Salle 2, en ligne..."/>
              </div>
            </div>
            <div style={{display:'flex',gap:'0.75rem',marginTop:'1.25rem'}}>
              <button className="admin-btn admin-btn--primary" onClick={handleSaveSlot} disabled={saving}>
                {saving ? <span className="admin-spinner-sm"/> : <><Save size={14}/> Enregistrer</>}
              </button>
              <button className="admin-btn admin-btn--secondary" onClick={() => setEditing(null)}>Annuler</button>
            </div>
          </div>
        </div>
      )}

      {/* Tableau planning */}
      {loading ? <div className="admin-page-loading"><div className="admin-spinner"/></div> : (
        <div className="admin-planning-table-wrap">
          <table className="admin-planning-table">
            <thead>
              <tr>
                <th className="admin-planning-th--slot">Créneau</th>
                {dates.map((d, i) => (
                  <th key={i} className={`admin-planning-th ${d.toDateString() === new Date().toDateString() ? 'today' : ''}`}>
                    <span className="admin-planning-th__day">{JOURS[i]}</span>
                    <span className="admin-planning-th__date">
                      {d.toLocaleDateString('fr-FR', { day:'numeric', month:'short' })}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {slots.map((slot, si) => (
                <tr key={si}>
                  <td className="admin-planning-slot-label">{slot}</td>
                  {JOURS.map((jour, ji) => {
                    const key = `${jour}_${slot}`;
                    const cell = planning[key];
                    const couleur = cell ? COULEURS[cell.module] : null;
                    const isToday = dates[ji].toDateString() === new Date().toDateString();
                    return (
                      <td key={ji} className={`admin-planning-cell ${isToday ? 'today' : ''}`}>
                        {cell ? (
                          <div className="admin-planning-event" style={{
                            background: couleur?.bg,
                            borderLeft: `3px solid ${couleur?.border}`,
                          }}>
                            <div className="admin-planning-event__module" style={{color: couleur?.text}}>
                              {cell.module}
                            </div>
                            <div className="admin-planning-event__trainer">👤 {cell.trainer}</div>
                            {cell.niveau && <div className="admin-planning-event__niveau">
                              <span className="admin-badge admin-badge--niveau" style={{fontSize:'0.6rem'}}>{cell.niveau}</span>
                            </div>}
                            {cell.notes && <div className="admin-planning-event__notes">📍 {cell.notes}</div>}
                            {canEdit && (
                              <div className="admin-planning-event__actions">
                                <button onClick={() => openEdit(jour, slot)} title="Modifier">
                                  <Edit2 size={11}/>
                                </button>
                                <button onClick={() => handleDeleteSlot(jour, slot)} title="Supprimer">
                                  <X size={11}/>
                                </button>
                              </div>
                            )}
                          </div>
                        ) : (
                          canEdit && (
                            <button className="admin-planning-add" onClick={() => openEdit(jour, slot)}>
                              + Ajouter
                            </button>
                          )
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Légende */}
      <div className="admin-planning-legend">
        {MODULES.map(m => {
          const c = COULEURS[m];
          return (
            <div key={m} className="admin-planning-legend__item">
              <span style={{width:12,height:12,borderRadius:3,background:c.bg,border:`2px solid ${c.border}`,display:'inline-block'}}/>
              <span style={{color:c.text,fontSize:'0.8rem',fontWeight:600}}>{m}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}